import React, {useCallback, useEffect, useState} from "react";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";
import {Link} from "react-router-dom";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
import {makeStyles} from "@material-ui/core/styles";
import {withFirebase} from "../components/Firebase";
import LockOutlinedIcon from "@material-ui/icons/LockOutlined";
import Container from "@material-ui/core/Container";
import logo from "../assets/images/logo-reqmaster.png";
import {
    doSignInJobWithCurrentUser,
    getCurrentAuthUser,
    signInWithEmailAndPassword,
    signOut
} from '../actions/auth';
import {formatExpirationDate, isUserExpired, toDate, userIsAdmin} from '../helpers/helper-functions';
import {History} from 'history';
import {useIsUserLoggedIn} from '../firebase-listeners/auth';
import {IconButton, InputAdornment} from "@material-ui/core";
import {Visibility, VisibilityOff} from "@material-ui/icons";
import {FirestoreUser} from "../firebase-listeners/interface";
import firebase from "firebase/app";
import {getFirestoreUser, useCurrentFirestoreUser} from "../firebase-listeners/firestore-user";

const useStyles = makeStyles((theme) => ({
    paper: {
        marginTop: theme.spacing(8),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
    },
    avatar: {
        margin: theme.spacing(1),
        backgroundColor: theme.palette.secondary.main,
    },
    form: {
        width: "100%", // Fix IE 11 issue.
        marginTop: theme.spacing(1),
    },
    submit: {
        margin: theme.spacing(3, 0, 2),
    },
}));

export type Props = {
    history: History;
}

const setFirebasePersistence = async () => {
    const remember = localStorage.getItem(`reqmaster-remember-me`);
    const auth = firebase.auth();
    const persistence = remember
        ? firebase.auth.Auth.Persistence.LOCAL
        : firebase.auth.Auth.Persistence.SESSION;
    return auth.setPersistence(persistence);
};

export const SignIn = (props: Props) => {
    const classes = useStyles();
    const isLoggedIn = useIsUserLoggedIn();
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [user, setUser] = useState<FirestoreUser | null>(null);
    const [rememberMe, setRememberMe] = useState<boolean>(localStorage.getItem('reqmaster-remember-me') === 'true');

    const finishLogin = useCallback((user) => {
        if (!user?.uid) {
            return
        }
        const queryParams = user.firstname ? `?firstname=${user.firstname}` : '';
        if (userIsAdmin(user.uid)) {
            props.history.push(`/student/admin${queryParams}`);
        } else {

            if (!isUserExpired(user)) {
                props.history.push(`/student/home${queryParams}`);
            }
        }
    }, [props.history])

    useEffect(() => {
        const remember = localStorage.getItem(`reqmaster-remember-me`);
        setFirebasePersistence().then(() => getCurrentAuthUser()).then(async user => {
            if (remember) {
                if (user) {
                    const firestoreUser = await doSignInJobWithCurrentUser(user);
                    finishLogin(firestoreUser)
                }
            }
        })
    }, [finishLogin, props.history]);

    const userExpired = useCallback(() => {
        return isUserExpired(user)
    }, [user]);

    const toggleRememberMe = () => {
        const remember = !rememberMe
        setRememberMe(remember);
        localStorage.setItem('reqmaster-remember-me', `${remember}`);
    }

    const signIn = useCallback(async () => {
        setSubmitting(true);
        try {
            const user = await signInWithEmailAndPassword({email, password});
            const firestoreUser = await getFirestoreUser(user.uid, false);
            setUser(user);
            if (userIsAdmin(user.uid)) {
                finishLogin(firestoreUser)
                setSubmitting(false);
                return;
            }

            if (user.licenseExpirationDate) {
                const expirationDate = toDate(user.licenseExpirationDate);
                const today = new Date();

                const timeBeforeExpiration = expirationDate.getTime() - today.getTime();

                // already expired
                if (timeBeforeExpiration < 0) {
                    alert(`Please renew your license! Your license expired on ${formatExpirationDate(user.licenseExpirationDate)}!`);
                    setSubmitting(false);
                    await signOut()
                    props.history.push(`/student/signin`);
                    return;
                }

                const expired7DaysOrLessFromNow = timeBeforeExpiration <= 7 * 24 * 60 * 60 * 1000;
                if (expired7DaysOrLessFromNow) {
                    alert(`You have less than a week left! Your license will expire on ${formatExpirationDate(user.licenseExpirationDate)}!`);
                }
            }

            setSubmitting(false);
            finishLogin(user)
        } catch (e: any) {
            if (e.code === 'auth/wrong-password') {
                alert('Invalid username or password')
            } else if (e.message) {
                alert(e.message)
            } else {
                alert('An error occurred')
            }
        }

    }, [email, finishLogin, password, props.history]);

    useEffect(() => {
        if (isLoggedIn && !submitting && user && !userExpired()) {
            signIn()
        }
    }, [isLoggedIn, props.history, signIn, submitting, user, userExpired]);

    const handleKeyPress = (e: { keyCode: number }) => {
        //it triggers by pressing the enter key
        if (e.keyCode === 13) {
            signIn();
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <CssBaseline/>
            <img
                alt={"Sign in"}
                src={logo}
                style={{
                    width: "270px",
                    height: "50px",
                    position: "absolute",
                    left: "10px",
                    top: "10px",
                }}
            />
            <div className={classes.paper}>
                <Avatar className={classes.avatar}>
                    <LockOutlinedIcon/>
                </Avatar>
                <Typography component="h1" variant="h5">
                    Sign in
                </Typography>
                <form className={classes.form} noValidate>
                    <TextField
                        onKeyDown={handleKeyPress}
                        onChange={e => setEmail(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        id="email"
                        label="Email Address"
                        name="email"
                        autoComplete="email"
                        autoFocus
                    />
                    <TextField
                        onKeyDown={handleKeyPress}
                        onChange={e => setPassword(e.target.value)}
                        variant="outlined"
                        margin="normal"
                        required
                        fullWidth
                        name="password"
                        label="Password"
                        type={showPassword ? "text" : "password"}
                        id="password"
                        autoComplete="current-password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(show => !show)}
                                        edge="end"
                                        aria-label="toggle password visibility"
                                    >
                                        {showPassword ? <Visibility/> : <VisibilityOff/>}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={rememberMe} onClick={toggleRememberMe} color="primary"/>}
                        label="Remember me"
                    />
                    <Button
                        type="button"
                        fullWidth
                        variant="contained"
                        color="primary"
                        className={classes.submit}
                        onClick={() => signIn()}
                    >
                        Sign In
                    </Button>
                    <Grid container>
                        <Grid item xs>
                            <Link to={{pathname: "/student/reset-password"}}>
                                Reset password
                            </Link>
                        </Grid>
                    </Grid>
                </form>
            </div>
        </Container>
    );
}

export default withFirebase(SignIn);
